import { useEffect, useMemo, useRef, useState } from 'react';
import type { V2Evidence } from '../../domain/evidence';
import { useT, type MessageKey } from '../../i18n/i18n';
import { removeEvidenceRecordAction } from '../../state/captureFlow';
import { useMediaUrl } from '../../state/mediaUrls';
import { useBackHandler } from '../../ui/backStack';
import { Button, ConfirmSheet, Icon, IconButton, Portal } from '../../ui/components';
import { useBodyScrollLock } from '../../ui/scrollLock';
import { createGestureController, IDENTITY, ZOOM_SNAP, type Point, type Transform } from './lightboxGestures';
import { PhotoNoteSheet } from './PhotoNoteSheet';
import { AnnotateSheet, AnnotationOverlay } from './AnnotateSheet';
import { isInspectionLockedById, inspectionStore } from '../../state/inspectionStore';

// Photo viewer (Authority §8.5; Round 2 §U): dark stage, close at the top, prev / next inside the
// same group, pinch-to-zoom with pan, double-tap zoom, one-finger swipe navigation at 1×, the
// photo's note (always editable from here too) and album ledger, and the actions — edit note,
// remove from the current item / finding (link only) or delete the app record. The system album
// is never touched — the copy there stays (OC §6.1.2) and the confirmation says so. Body is
// scroll-locked while open; the viewer renders through the overlay portal.
export interface LightboxContext {
  itemId: string | null;
  findingId: string | null;
}

const ALBUM_STATE_KEYS: Record<V2Evidence['albumSaveState'], MessageKey> = {
  pending: 'photo.albumStatePending',
  saved: 'photo.albumStateSaved',
  failed: 'photo.albumStateFailed',
  unavailable: 'photo.albumStateUnavailable',
  notRequired: 'photo.albumStateNotRequired'
};

// Minimal touch shape so tests can dispatch plain events with fake touch lists.
interface TouchLike {
  clientX: number;
  clientY: number;
}

function pointsOf(list: ArrayLike<TouchLike> | undefined, stage: HTMLElement | null): Point[] {
  if (!list || !stage) return [];
  const rect = stage.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const out: Point[] = [];
  for (let i = 0; i < list.length; i += 1) out.push({ x: list[i].clientX - cx, y: list[i].clientY - cy });
  return out;
}

export function Lightbox(props: {
  evidence: V2Evidence[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  context: LightboxContext;
  onUnlink?: (evidenceId: string) => Promise<void> | void;
}) {
  const { t } = useT();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [annotateOpen, setAnnotateOpen] = useState(false);
  // Where the photo sits in the stage (layout box, before the zoom transform), so the marks drawn on it
  // (Owner item 22) can be laid exactly over it and follow the same zoom.
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageBox, setImageBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [transform, setTransform] = useState<Transform>(IDENTITY);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const current = props.evidence[props.index] ?? null;
  const url = useMediaUrl(current ? current.id : '', 'full');
  useBodyScrollLock();
  // System Back (Android) closes the viewer, like Escape and the top Close (src/ui/backStack.ts).
  useBackHandler(props.onClose);

  const latest = useRef({ index: props.index, total: props.evidence.length, onIndexChange: props.onIndexChange });
  latest.current = { index: props.index, total: props.evidence.length, onIndexChange: props.onIndexChange };

  const gestures = useMemo(
    () =>
      createGestureController({
        onTransform: setTransform,
        onNavigate: (direction) => {
          const { index, total, onIndexChange } = latest.current;
          if (direction === 'next' && index < total - 1) onIndexChange(index + 1);
          if (direction === 'prev' && index > 0) onIndexChange(index - 1);
        }
      }),
    []
  );

  // A new photo always opens at 1×.
  useEffect(() => {
    gestures.reset();
  }, [gestures, props.index]);

  useEffect(() => {
    if (!current) props.onClose();
  }, [current, props]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') props.onClose();
      if (event.key === 'ArrowLeft' && props.index > 0) props.onIndexChange(props.index - 1);
      if (event.key === 'ArrowRight' && props.index < props.evidence.length - 1) props.onIndexChange(props.index + 1);
      if (event.key === '+' || event.key === '=') gestures.zoomTo(gestures.transform().scale * 1.25);
      if (event.key === '-') gestures.zoomTo(gestures.transform().scale / 1.25);
      if (event.key === '0') gestures.reset();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props, gestures]);

  // Native, non-passive touch handling: pinch / pan must cancel the default (no page scroll, no
  // browser zoom); the stage measures itself for the pan bounds.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const measure = () => {
      const rect = stage.getBoundingClientRect();
      gestures.setStageSize(rect.width, rect.height);
    };
    measure();
    const onStart = (event: TouchEvent) => {
      measure();
      gestures.start(pointsOf(event.touches, stage));
      if (event.touches.length > 1 && event.cancelable) event.preventDefault();
    };
    const onMove = (event: TouchEvent) => {
      gestures.move(pointsOf(event.touches, stage));
      if ((gestures.consuming() || event.touches.length > 1) && event.cancelable) event.preventDefault();
    };
    const onEnd = (event: TouchEvent) => gestures.end(pointsOf(event.touches, stage));
    const onCancel = () => gestures.cancel();
    stage.addEventListener('touchstart', onStart, { passive: false });
    stage.addEventListener('touchmove', onMove, { passive: false });
    stage.addEventListener('touchend', onEnd, { passive: true });
    stage.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      stage.removeEventListener('touchstart', onStart);
      stage.removeEventListener('touchmove', onMove);
      stage.removeEventListener('touchend', onEnd);
      stage.removeEventListener('touchcancel', onCancel);
    };
  }, [gestures]);

  useEffect(() => {
    const measure = () => {
      const img = imageRef.current;
      setImageBox(img && img.offsetWidth > 0 ? { left: img.offsetLeft, top: img.offsetTop, width: img.offsetWidth, height: img.offsetHeight } : null);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [url]);

  if (!current) return null;
  const marks = current.annotations ?? [];
  const locked = isInspectionLockedById(inspectionStore.get(), current.inspectionId);

  async function handleDelete() {
    if (!current) return;
    setConfirmDelete(false);
    const removedIndex = props.index;
    await removeEvidenceRecordAction(current.id);
    if (props.evidence.length <= 1) props.onClose();
    else props.onIndexChange(Math.max(0, Math.min(removedIndex, props.evidence.length - 2)));
  }

  function onDoubleClick(event: { clientX: number; clientY: number }) {
    const [point] = pointsOf([event], stageRef.current);
    if (point) gestures.toggleZoomAt(point);
  }

  const canUnlink = props.onUnlink !== undefined && (props.context.itemId !== null || props.context.findingId !== null);
  const zoomed = transform.scale >= ZOOM_SNAP;

  return (
    <Portal>
      <div className="lightbox" role="dialog" aria-label={t('photo.viewer')} data-testid="lightbox" data-overlay="lightbox">
        <div className="lightbox-top">
          <span className="lightbox-counter">{t('photo.counter', { index: props.index + 1, total: props.evidence.length })}</span>
          <div className="lightbox-zoom" data-testid="lightbox-zoom-tools">
            <IconButton icon="remove" label={t('photo.zoomOut')} onClick={() => gestures.zoomTo(transform.scale / 1.5)} disabled={!zoomed} tone="soft" testId="lightbox-zoom-out" />
            <button type="button" className="lightbox-zoom-value" onClick={() => gestures.reset()} disabled={!zoomed} data-testid="lightbox-zoom-reset" aria-label={t('photo.zoomReset')}>
              {`${Math.round(transform.scale * 100)}%`}
            </button>
            <IconButton icon="add" label={t('photo.zoomIn')} onClick={() => gestures.zoomTo(transform.scale * 1.5)} tone="soft" testId="lightbox-zoom-in" />
          </div>
          <button type="button" onClick={props.onClose} data-testid="lightbox-close">
            {t('shell.close')}
          </button>
        </div>
        <div
          ref={stageRef}
          className={`lightbox-stage ${zoomed ? 'lightbox-stage-zoomed' : ''}`}
          onDoubleClick={onDoubleClick}
          data-testid="lightbox-stage"
          data-scale={transform.scale.toFixed(2)}
          data-zoomed={zoomed ? 'true' : 'false'}
        >
          {url ? (
            <img
              ref={imageRef}
              src={url}
              alt=""
              draggable={false}
              onLoad={(event) => {
                const img = event.currentTarget;
                setImageBox({ left: img.offsetLeft, top: img.offsetTop, width: img.offsetWidth, height: img.offsetHeight });
              }}
              style={{ transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})` }}
              data-testid="lightbox-image"
            />
          ) : (
            <span>{t('shell.loading')}</span>
          )}
          {url && imageBox && marks.length > 0 ? (
            <div className="lightbox-marks" style={{ left: imageBox.left, top: imageBox.top, width: imageBox.width, height: imageBox.height, transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})` }}>
              <AnnotationOverlay evidence={current} annotations={marks} testId="lightbox-marks" />
            </div>
          ) : null}
        </div>
        <p className="lightbox-hint" data-testid="lightbox-hint">
          {t('photo.pinchHint')}
        </p>
        <div className="lightbox-nav">
          <button type="button" disabled={props.index === 0} onClick={() => props.onIndexChange(props.index - 1)} data-testid="lightbox-prev">
            <Icon name="chevron_right" className="icon-flip" /> {t('photo.previous')}
          </button>
          <button type="button" disabled={props.index >= props.evidence.length - 1} onClick={() => props.onIndexChange(props.index + 1)} data-testid="lightbox-next">
            {t('photo.next')} <Icon name="chevron_right" />
          </button>
        </div>
        <p className="lightbox-meta" data-testid="lightbox-meta">
          {current.source === 'camera' ? t('photo.sourceCamera') : t('photo.sourceLibrary')} · {t(ALBUM_STATE_KEYS[current.albumSaveState])}
          {current.albumSaveDetail.length > 0 ? ` · ${current.albumSaveDetail}` : ''}
        </p>
        <p className="lightbox-note" data-testid="lightbox-note">
          {current.caption.length > 0 ? (
            <>
              <strong>{t('photo.notePrefix')}</strong> {current.caption}
            </>
          ) : (
            t('photo.addPhotoNote')
          )}
        </p>
        <div className="lightbox-actions">
          {!locked ? (
            <Button variant="secondary" icon="draw" onClick={() => setAnnotateOpen(true)} testId="lightbox-annotate">
              {marks.length > 0 ? t('annotate.edit') : t('annotate.open')}
            </Button>
          ) : null}
          <Button variant="secondary" icon="edit_note" onClick={() => setNoteOpen(true)} testId="lightbox-note-edit">
            {current.caption.length > 0 ? t('photo.editNote') : t('photo.addPhotoNote')}
          </Button>
          {canUnlink ? (
            <Button variant="secondary" onClick={() => void props.onUnlink?.(current.id)} testId="lightbox-unlink">
              {t('photo.removeFromItem')}
            </Button>
          ) : null}
          <Button variant="danger" onClick={() => setConfirmDelete(true)} testId="lightbox-delete">
            {t('photo.deletePhoto')}
          </Button>
        </div>
        {noteOpen ? <PhotoNoteSheet evidence={current} layer="top" onClose={() => setNoteOpen(false)} /> : null}
        {annotateOpen ? <AnnotateSheet evidence={current} onClose={() => setAnnotateOpen(false)} /> : null}
        {confirmDelete ? (
          <ConfirmSheet
            title={t('photo.deleteTitle')}
            body={t('photo.deleteBody')}
            confirmLabel={t('photo.deleteConfirm')}
            danger
            onConfirm={() => void handleDelete()}
            onCancel={() => setConfirmDelete(false)}
          />
        ) : null}
      </div>
    </Portal>
  );
}
